<script>
import theme from "#build/ui/avatar";
</script>

<script setup>
import { ref, computed, watch } from "vue";
import { Primitive, Slot } from "reka-ui";
import { useAppConfig } from "#imports";
import ImageComponent from "#build/ui-image-component";
import { useAvatarGroup } from "../composables/useAvatarGroup";
import { tv } from "../utils/tv";
import UIcon from "./Icon.vue";
import UChip from "./Chip.vue";
defineOptions({ inheritAttrs: false });
const props = defineProps({
  as: { type: null, required: false, default: "span" },
  src: { type: String, required: false },
  alt: { type: String, required: false },
  icon: { type: String, required: false },
  text: { type: String, required: false },
  size: { type: null, required: false },
  chip: { type: [Boolean, Object], required: false },
  class: { type: null, required: false },
  style: { type: null, required: false },
  ui: { type: null, required: false }
});
const fallback = computed(() => props.text || (props.alt || "").split(" ").map((word) => word.charAt(0)).join("").substring(0, 2));
const appConfig = useAppConfig();
const { size } = useAvatarGroup(props);
const ui = computed(() => tv({ extend: tv(theme), ...appConfig.ui?.avatar || {} })({
  size: size.value
}));
const sizePx = computed(() => ({
  "3xs": 16,
  "2xs": 20,
  "xs": 24,
  "sm": 28,
  "md": 32,
  "lg": 36,
  "xl": 40,
  "2xl": 44,
  "3xl": 48
})[props.size || "md"]);
const error = ref(false);
watch(() => props.src, () => {
  if (error.value) {
    error.value = false;
  }
});
function onError() {
  error.value = true;
}
</script>

<template>
  <component
    :is="props.chip ? UChip : Primitive"
    :as="as"
    v-bind="props.chip ? typeof props.chip === 'object' ? { inset: true, ...props.chip } : { inset: true } : {}"
    :class="ui.root({ class: [props.ui?.root, props.class] })"
    :style="props.style"
  >
    <component
      :is="ImageComponent"
      v-if="src && !error"
      role="img"
      :src="src"
      :alt="alt"
      :width="sizePx"
      :height="sizePx"
      v-bind="$attrs"
      :class="ui.image({ class: props.ui?.image })"
      @error="onError"
    />

    <Slot v-else v-bind="$attrs">
      <slot>
        <UIcon v-if="icon" :name="icon" :class="ui.icon({ class: props.ui?.icon })" />
        <span v-else :class="ui.fallback({ class: props.ui?.fallback })">{{ fallback || "\xA0" }}</span>
      </slot>
    </Slot>
  </component>
</template>
